import { Separator } from "@/components/ui/separator.tsx";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet.tsx";
import { cn } from "@/lib/utils";
import { Cross1Icon } from "@radix-ui/react-icons";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { ChevronLeft } from "lucide-react";
import { ComponentPropsWithoutRef, ReactNode } from "react";
import { DialogProps } from "vaul";

interface StyledDrawerProps {
  children: ReactNode;
  title: ReactNode;
  trigger?: ReactNode;
  className?: string;
  closeButton?: ReactNode;
  /** Contenu optionnel affiché dans le header, juste avant le bouton de fermeture. */
  headerActions?: ReactNode;
  footer?: ReactNode;
  sheetProps?: DialogProps;
  description?: ReactNode;
  arrowClose?: boolean;
  maxWidth?: string; // e.g., "sm:max-w-5xl", "w-[800px]", etc.
  hideSeparator?: boolean;
  /** Keep drawer content mounted when closed (preserves RHF fields used in the drawer). */
  keepContentMounted?: boolean;
  /**
   * Props transmis tels quels au `SheetContent` (Radix Dialog Content) : permet
   * p.ex. d'empêcher la fermeture sur « interact/focus outside »
   * (`onInteractOutside={(e) => e.preventDefault()}`) quand le contenu monte des
   * éléments qui déplacent le focus (formulaires riches, Select…).
   */
  contentProps?: ComponentPropsWithoutRef<typeof SheetContent>;
}

const StyledDrawer = ({
  arrowClose = false,
  title,
  children,
  trigger,
  className,
  closeButton,
  headerActions,
  footer,
  sheetProps,
  description,
  maxWidth,
  hideSeparator = false,
  keepContentMounted = false,
  contentProps,
}: StyledDrawerProps) => {
  const isOpen = sheetProps?.open ?? false;

  return (
    <Sheet {...sheetProps}>
      {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
      <SheetContent
        side="right"
        showPrimitiveCross={false}
        forceMount={keepContentMounted || undefined}
        className={cn(
          "max-h-screen flex flex-col gap-0",
          className,
          maxWidth || "sm:max-w-sm",
          keepContentMounted &&
            !isOpen &&
            "invisible pointer-events-none data-[state=closed]:animate-none",
        )}
        {...contentProps}
      >
        {title && (
          <VisuallyHidden>
            <SheetTitle>{title}</SheetTitle>
          </VisuallyHidden>
        )}
        <div className="w-full self-stretch justify-start items-center gap-4 inline-flex pb-4 p-6">
          <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
            <div className="self-stretch text-[#0c111d] text-lg font-semibold font-['Raleway'] leading-normal flex items-center">
              {arrowClose && (
                <SheetClose>
                  <ChevronLeft className="h-6 w-6 relative mr-4" />
                </SheetClose>
              )}
              {title}
            </div>
          </div>
          {headerActions}
          <SheetClose>
            {closeButton ? (
              closeButton
            ) : (
              <Cross1Icon className="h-4 w-4 relative text-gray-400" />
            )}
          </SheetClose>
        </div>
        {!hideSeparator && <Separator />}
        <div className="pt-4 flex flex-col flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-4">
          {children}
        </div>
        <SheetFooter>{footer}</SheetFooter>
        {description && (
          <VisuallyHidden>
            <SheetDescription>{description}</SheetDescription>
          </VisuallyHidden>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default StyledDrawer;
